import React, { useState } from 'react';
import { 
  Building2, 
  Search, 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle2, 
  FileText,
  Calendar,
  ExternalLink,
  Info
} from 'lucide-react';

export const AbnCheckerPage: React.FC = () => {
  const [abnQuery, setAbnQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [result, setResult] = useState<any | null>(null);

  const sampleAbns = [
    { label: 'Scam Store (Registered 4 days ago)', abn: '88 192 840 192' },
    { label: 'Legitimate Australian Retailer', abn: '48 123 123 124' },
    { label: 'Cancelled Entity (Inactive ABN)', abn: '11 000 000 000' }
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!abnQuery.trim()) return;

    setIsSearching(true);
    setTimeout(() => {
      const clean = abnQuery.replace(/\s+/g, '');
      const isNewScam = clean.includes('88192');
      const isCancelled = clean.includes('11000');

      if (isNewScam) {
        setResult({
          abn: '88 192 840 192',
          entityName: 'DIRECT CLEARANCE APPLIANCES PTY LTD',
          status: 'Active (Registered 4 Days Ago)',
          registeredDate: '16 August 2026',
          gstStatus: 'Not Registered for GST',
          acncCharity: false,
          entityType: 'Australian Private Company',
          location: 'Parramatta NSW 2150',
          riskLevel: 'HIGH',
          riskNotes: 'Warning: Brand new entity registered 4 days ago. High likelihood of disposable scam storefront.'
        });
      } else if (isCancelled) {
        setResult({
          abn: '11 000 000 000',
          entityName: 'PACIFIC IMPORT LOGISTICS',
          status: 'CANCELLED (Inactive since 2022)',
          registeredDate: '14 May 2018',
          gstStatus: 'Cancelled',
          acncCharity: false,
          entityType: 'Partnership',
          location: 'Brisbane QLD 4000',
          riskLevel: 'CRITICAL',
          riskNotes: 'Do not transfer funds. This business registration has been cancelled on the Australian Business Register.'
        });
      } else {
        setResult({
          abn: '48 123 123 124',
          entityName: 'SYDNEY RETAIL ENTERPRISES PTY LTD',
          status: 'Active (Established 12 Years)',
          registeredDate: '10 March 2014',
          gstStatus: 'Currently Registered for GST',
          acncCharity: false,
          entityType: 'Australian Private Company',
          location: 'Sydney NSW 2000',
          riskLevel: 'LOW',
          riskNotes: 'Verified historical ABR registration with active GST compliance.'
        });
      }
      setIsSearching(false);
    }, 500);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-bold border border-emerald-800">
          <Building2 className="w-3.5 h-3.5" />
          <span>AUSTRALIAN BUSINESS REGISTER (ABR) LOOKUP</span>
        </div>
        <h1 className="text-3xl font-extrabold text-white">Australian Business Number (ABN) Scammer Check</h1>
        <p className="text-xs text-slate-400 max-w-xl mx-auto">
          Verify company registration age, GST status, and trading credentials to detect disposable scam storefronts and fake contractors.
        </p>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xl">
        <form onSubmit={handleSearch} className="space-y-3">
          <label className="block text-xs font-bold text-slate-300">Enter 11-Digit ABN or Business Name:</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                value={abnQuery}
                onChange={(e) => setAbnQuery(e.target.value)}
                placeholder="e.g. 88 192 840 192 or company name..."
                required
                className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono text-xs focus:outline-none focus:border-emerald-500"
              />
            </div>
            <button
              type="submit"
              disabled={isSearching}
              className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl font-bold text-xs shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              {isSearching ? 'Querying ABR Database...' : 'Verify ABN'}
            </button>
          </div>
        </form>

        <div className="pt-2 border-t border-slate-800 flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-slate-400">Quick Test Cases:</span>
          {sampleAbns.map((s, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setAbnQuery(s.abn)}
              className="px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 text-slate-300 hover:border-slate-700"
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {result && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl animate-fade-in text-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-4">
            <div>
              <div className="text-lg font-bold text-white">{result.entityName}</div>
              <div className="text-slate-400 font-mono">ABN: {result.abn} • {result.location}</div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-bold border ${
              result.riskLevel === 'HIGH' || result.riskLevel === 'CRITICAL'
                ? 'bg-rose-950 text-rose-300 border-rose-800'
                : 'bg-emerald-950 text-emerald-300 border-emerald-800'
            }`}>
              {result.riskLevel} RISK
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 space-y-0.5">
              <span className="text-slate-400">Entity Status:</span>
              <div className="font-bold text-white">{result.status}</div>
            </div>
            <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 space-y-0.5">
              <span className="text-slate-400">Registration Date:</span>
              <div className="font-bold text-white font-mono">{result.registeredDate}</div>
            </div>
            <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 space-y-0.5">
              <span className="text-slate-400">GST Registration:</span>
              <div className="font-bold text-white">{result.gstStatus}</div>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
            <h4 className="font-bold text-white flex items-center gap-2">
              <Info className="w-4 h-4 text-emerald-400" />
              <span>Fraud Risk Assessment</span>
            </h4>
            <p className="text-slate-300 leading-relaxed">{result.riskNotes}</p>
          </div>
        </div>
      )}
    </div>
  );
};
